import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { catchError, exhaustMap, mergeMap } from 'rxjs/operators';
import {
  ActiveTenantStore,
  CoreService,
  LoginSuccess,
  User,
} from '@arc/arc-shared-frontend';

/**
 * Factory that creates a reusable NgRx functional effect that loads user info
 * after a platform JWT is obtained.
 *
 * The effect listens for a configurable trigger action carrying an accessToken,
 * reads the active tenant from the store, calls CoreService.loadUserInfo with
 * the token and tenant ID, then dispatches LoginSuccess on completion or the
 * provided error action on failure.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiLoadUserEffect = createLoadUserEffect({
 *     triggerAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *     errorAction:   MakePaymentAuthApiActions.loadUserError,
 *   });
 */
export function createLoadUserEffect<
  A extends Action<string> & { accessToken: string },
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  errorAction: ActionCreator;
  successAction?: ActionCreator<string, Creator<[], Action<string>>>;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      coreService = inject(CoreService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => store.select(ActiveTenantStore)),
        exhaustMap(([action, activeTenant]) =>
          coreService
            .loadUserInfo(
              action.accessToken,
              { tenantId: activeTenant.tenantId },
              true,
            )
            .pipe(
              mergeMap((response) => {
                const loginSuccess = new LoginSuccess({
                  user: response.user as User,
                  activeTenant,
                  activeRole: null,
                  logoutUrl: null,
                });
                return config.successAction
                  ? of(loginSuccess, config.successAction())
                  : of(loginSuccess);
              }),
              catchError((error: HttpErrorResponse) =>
                of(config.errorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
